// The drain banner (ui-map-interface.md §3.4.1, R2094).
//
// Seaquake stage 3 locks the scores, and the drain of the settlement then
// takes the board for five WALL minutes (numbers.md §15, R2091, R2092):
// the shocks land faster and harder, the holds fall in and the colour
// drains off the map. The season summary card opens when the drain ends.
// Until then the chrome stays, and this band stands on the map: it says
// that the sea takes the board, and it counts down to the card.
//
// The band reads the settlement clock of the wire (`SettlementClock`,
// R2093) through the state: it stands while a drain end is known and has
// not passed, and it goes in the card phase. It holds no control, so a
// touch and a pointer reach the map through it. The drain line under the
// words shows the share of the drain that is left. Reduced motion keeps
// the band and drops its entrance.

import { settlement as S } from '../gen/strings';
import { reducedMotion } from '../render/motion';
import type { GameState } from '../state';
import { mmss } from '../locale/clock';
import { clamp01 } from '../render/math';

/** What the band needs from the page. */
export interface SettlementDeps {
  /** The element the band stands in: the chrome's own shell. */
  host: HTMLElement;
  state: GameState;
}

/** The band is on the glass while this holds: a drain end is known and
 *  the card phase has not opened. */
function drainShowing(state: GameState): boolean {
  return state.drainEndsMs > 0 && !state.settlementCard();
}

/** The share of the drain still to run, 0..1, against the drain total of
 *  the hello's rules. A total of 0 (before the hello) reads as a full
 *  line, the rule of the bar's foot (§8). */
export function drainShare(leftSec: number, totalSec: number): number {
  if (!(totalSec > 0)) return 1;
  return clamp01(leftSec / totalSec);
}

export class SettlementBanner {
  private el: HTMLElement | null = null;
  private clockEl: HTMLElement | null = null;
  private fillEl: HTMLElement | null = null;
  private timer = 0;
  private lastClock = '';

  constructor(private readonly d: SettlementDeps) {
    d.state.on(() => this.sync());
  }

  /** Presentation-only; the tests read it. */
  get showing(): boolean { return this.el !== null; }

  /** Puts the band on the glass or takes it off, after a state change. */
  sync(): void {
    if (!drainShowing(this.d.state)) {
      this.close();
      return;
    }
    if (!this.el) this.open();
    this.tick();
  }

  private open(): void {
    const root = document.createElement('div');
    root.id = 'settlement';
    root.className = 'panel';
    root.setAttribute('role', 'region');
    root.setAttribute('aria-label', S.kick);
    if (reducedMotion()) root.classList.add('still');
    root.innerHTML = `<div class="sb-kick"></div><div class="sb-line"></div>`
      + `<div class="sb-drain" aria-hidden="true"><i></i></div>`;
    const kick = root.querySelector<HTMLElement>('.sb-kick');
    if (kick) kick.textContent = S.kick;
    this.clockEl = root.querySelector<HTMLElement>('.sb-line');
    this.fillEl = root.querySelector<HTMLElement>('.sb-drain i');
    this.d.host.appendChild(root);
    this.el = root;
    this.lastClock = '';
    // The clock moves each wall second with no frame from the server, so
    // the band ticks on its own while it stands.
    this.timer = window.setInterval(() => this.sync(), 1000);
  }

  private close(): void {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = 0;
    this.el?.remove();
    this.el = null;
    this.clockEl = null;
    this.fillEl = null;
  }

  /** The countdown and the drain line. */
  private tick(): void {
    const st = this.d.state;
    const left = st.drainLeftSec();
    const clock = mmss(Math.ceil(left));
    if (clock !== this.lastClock && this.clockEl) {
      this.lastClock = clock;
      this.clockEl.textContent = S.line({ left: clock });
    }
    if (this.fillEl) {
      const share = drainShare(left, st.rules.settlementDrainSec);
      this.fillEl.style.transform = `scaleX(${share})`;
    }
  }
}
